import React, { useState } from "react";
import { useSelector } from "react-redux";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";
// eslint-disable-next-line

import { format } from "date-fns";
import lodashGet from "lodash/get";

import { useDeepEffect, useDeepMemo, useQuery, useSetting, useTenant, useUnit } from "../../../../hooks";
import SettingKeys from "../../../../utils/SettingKeys";
import {
  getDistinctValues,
  getWeightedMean,
  isEmpty,
  isFalse,
  isTrue,
  listFromCsv,
  mapFromCsv,
  signedValue,
  toFixed,
  toFixedSigned
} from "../../../../utils/helpers";
import { AlertParemeter, SpreadingType, Entity, MarkerType } from "../../../../utils/types";
import { OrderFlowType, UOM } from "../../../../constants";
import { findSizeBreakupList, getCutOrderFlowInfoList, getOrderFlowList } from "../../../../helpers/orderHelper";
import { getCutOrderRows, getMarkerList } from "../../../cutplan/cutPlanHelper";
import { setDefault, totalPagesExp, minReqHeight } from "../../../../helpers/pdfHelper";
import { DataType, getEchartOptions } from "../microReportHelper";
import { getRepeatSize } from "../../../../helpers/warehouseHelper";
import { getReportFlowInfoList } from "./reportHelper";
const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const otherFields = [
  "fpo",
  "unitId",
  "fpoQty",
  "effPartName",
  "cutQty",
  "rejectedQty",
  "bomCu",
  "layCu",
  "planCu",
  "stdCu",
  "actualCu",
  "grossCu",
  "markerEfficiency",
  "itemCode",
  "itemColor",
  "allocated",
  "blanketQty",
  "issued",
  "returned",
  "excess",
  "shortage",
  "missing",
  "unused",
  "usedInMarker",
  "usedInPanel",
  "endLoss",
  "damaged",
  "netEndBits"
];

const getApprovedQty = (cutOrder, styleColor) => {
  if (isEmpty(cutOrder?.orderConfig?.approvedOrderCutQty)) return 0;
  const approvedOrderCutQtyMap = JSON.parse(cutOrder?.orderConfig?.approvedOrderCutQty || "{}");
  const approvedQtyMap = approvedOrderCutQtyMap[styleColor] || {};
  const approvedQty = Object.keys(approvedQtyMap).reduce((acc, key) => acc + approvedQtyMap[key], 0);
  return approvedQty;
};

const maxCharacterCount = size => {
  if (!size) return 0;
  const splits = size.split(" ");
  let max = 0;
  for (let i = 0; i < splits.length; i++) {
    if (splits[i].length > max) {
      max = splits[i].length;
    }
  }
  return max;
};

const getOtherInfoAndHighlight = (cutOrder, report, settings) => {
  const cutSetting = !isEmpty(cutOrder?.orderConfig?.cutSetting) ? JSON.parse(cutOrder.orderConfig.cutSetting) : {};
  // const groupingSetting = !isEmpty(cutOrder?.orderConfig?.groupingSetting) ? JSON.parse(cutOrder.orderConfig.groupingSetting) : {};

  let otherInfoList = [],
    highlightList = [];
  if (cutOrder && cutOrder.orderConfig && !isEmpty(report)) {
    const repeat = getRepeatSize({ repeatLength: report.repeatLength, repeatWidth: report.repeatWidth });
    otherInfoList = [
      // { title: "Width Wastage", subTitle: "", rightTitle: cutOrder.orderConfig.groupingWastage },
      {
        title: "Marker Type",
        subTitle: "",
        rightTitle: `${report.csv ? "CSV" : "Normal"} / ${cutSetting.fabricDirection}`
      },
      {
        title: "Design Repeat",
        subTitle: "",
        rightTitle: `${repeat ? `${repeat} cm` : "-"} `
      },
      { title: "Marker made by", subTitle: "", rightTitle: `${report.markerUpdatedBy || ""}` }
    ];
    if (settings && settings.length > 0) {
      const alerts = cutOrder.orderConfig.alerts ? JSON.parse(cutOrder.orderConfig.alerts) : [];
      const consumptionVariationAlert = alerts.find(alert => alert.parameter === AlertParemeter.CONSUMPTION_VARIATION.id) || {};
      const cutPlanQtyAlert = alerts.find(alert => alert.parameter === AlertParemeter.CUT_PLAN_QTY.id) || {};
      const lowMarkerEfficiencyAlert = alerts.find(alert => alert.parameter === AlertParemeter.MARKER_EFFICIENCY_VARIATION.id) || {};
      let setting;
      if (report.actualCu < report.orderingCu) {
        setting = settings.find(s => s.key === SettingKeys.CONSUMPTION_DECREASE);
        const decrease = parseFloat(toFixed((report.orderingCu - report.actualCu) * 100, 1));
        if (setting && decrease > parseFloat(setting.value)) {
          highlightList.push({
            title: "Decrease in Consumption",
            subTitle: consumptionVariationAlert.reasons && consumptionVariationAlert.reasons.join(", "),
            rightTitle: `${decrease} cm`
          });
        }
      }
      if (report.actualCu > report.orderingCu) {
        setting = settings.find(s => s.key === SettingKeys.CONSUMPTION_INCREASE);
        const increase = parseFloat(toFixed((report.actualCu - report.orderingCu) * 100, 1));
        if (setting && increase > parseFloat(setting.value)) {
          highlightList.push({
            title: "Increase in Consumption",
            subTitle: consumptionVariationAlert.reasons && consumptionVariationAlert.reasons.join(", "),
            rightTitle: `${increase} cm`
          });
        }
      }
      // const cutPercent = parseFloat(toFixed((report.cumCutQty * 100) / report.orderQty, 1));
      const cutPercent = parseFloat(toFixed((report.cutQty * 100) / report.orderQty, 1));
      if (report.noOfLots === report.lot && cutPercent < 100) {
        highlightList.push({
          title: "Low Cut percent",
          subTitle: cutPlanQtyAlert.reasons && cutPlanQtyAlert.reasons.join(", "),
          rightTitle: `${cutPercent}%`
        });
      }
      setting = settings.find(s => s.key === SettingKeys.HIGH_SPLITTING);
      const rollCount = report.rollCount || 0;
      const splitCount = report.splitCount || 0;
      const actualRollCount = rollCount - splitCount;
      const splitPercent = parseFloat(toFixed((splitCount * 100) / actualRollCount, 1));
      if (setting && splitPercent > parseFloat(setting.value)) {
        highlightList.push({
          title: "High number of roll splitting",
          rightTitle: `${splitCount}/${actualRollCount}`
        });
      }
      const deviationMap = JSON.parse(report.rollWidthDeviation || "{}");
      const deviation = Object.keys(deviationMap)
        .filter(key => deviationMap[key] > 0)
        .reduce((acc, key) => ({ ...acc, [key]: deviationMap[key] }), {});
      if (Object.keys(deviation).length > 2) {
        const keys = Object.keys(deviation)
          .map(key => parseFloat(key))
          .sort((a, b) => a - b);
        const deviationValue = `${keys[0]} cm to ${keys[keys.length - 1]} cm`;
        highlightList.push({
          title: "High width deviation",
          rightTitle: deviationValue
        });
      }

      setting = settings.find(s => s.key === SettingKeys.LESS_ACTUAL_WIDTH);
      const widthLessBy = setting && parseFloat(setting.value);
      if (report.actualWidth < report.width && parseFloat(toFixed(report.width - report.actualWidth, 1)) > widthLessBy) {
        highlightList.push({
          title: "Actual width less than BOM width",
          rightTitle: `-${parseFloat(toFixed(report.width - report.actualWidth, 1))} cm`
        });
      }

      setting = settings.find(s => s.key === SettingKeys.LOW_MARKER_EFFICIENCY);
      const sLowMarkerEff = setting && JSON.parse(setting.value);
      let isLowEfficiency = false;
      if (sLowMarkerEff) {
        if (report.repeatLength && report.repeatWidth) {
          const repeatValues = sLowMarkerEff["2DRepeat"];
          const key = Object.keys(repeatValues).find(key => {
            const start = parseInt(key.split("-")[0]);
            const end = parseInt(key.split("-")[1]);
            const repeat = report.repeatLength * report.repeatWidth;
            return repeat >= start && repeat < end;
          });
          if (report.markerEfficiency < repeatValues[key]) {
            isLowEfficiency = true;
          }
        } else if (report.repeatLength || report.repeatWidth) {
          const repeatValues = sLowMarkerEff["1DRepeat"];
          const key = Object.keys(repeatValues).find(key => {
            const start = parseInt(key.split("-")[0]);
            const end = parseInt(key.split("-")[1]);
            const repeat = report.repeatLength || report.repeatWidth;
            return repeat >= start && repeat < end;
          });
          if (report.markerEfficiency < repeatValues[key]) {
            isLowEfficiency = true;
          }
        } else {
          const repeatValues = sLowMarkerEff["NoRepeat"];
          if (cutOrder.orderConfig.csv && report.markerEfficiency < repeatValues["CSV"]) {
            isLowEfficiency = true;
          } else if (!cutOrder.orderConfig.csv && report.markerEfficiency < repeatValues["Normal"]) {
            isLowEfficiency = true;
          }
        }
      }
      if (isLowEfficiency) {
        highlightList.push({
          title: "Low Efficiency",
          subTitle: lowMarkerEfficiencyAlert.reasons && lowMarkerEfficiencyAlert.reasons.join(", "),
          rightTitle: `${toFixed(report.markerEfficiency)}%`
        });
      }
    }
  }
  return { otherInfoList, highlightList };
};

const colsFabUtilization = [
  { dataKey: "serialNo", title: "Sl. No." },
  { dataKey: "classification", title: "Classification" },
  { dataKey: "length", title: "Fabric Qty", align: "right" },
  { dataKey: "percentage", title: "Percentage", align: "right" },
  { dataKey: "remarks", title: "Remarks" }
];

const colsRemarks = [
  { dataKey: "params", title: "Parameter" },
  { dataKey: "remarks", title: "Reason/Remarks" },
  { dataKey: "user", title: "User" }
];

const useReconciliationDetail = props => {
  const { order, style, color, reportList, selectedKey, setSelectedKey } = props;

  const [cutOrderKey, setCutOrderKey] = useState(null);
  const [printing, setPrinting] = React.useState(false);

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ORDERS);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FLOW_INFO);
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const showStdCuVal = useSetting(SettingKeys.SHOW_STD_CU);
  const showStdCu = isTrue(showStdCuVal);

  const messages = useSelector(state => state?.i18n?.messages || {});
  const { allUnitList } = useUnit();
  const tenant = useTenant();

  const { data: settings = [] } = useQuery({ url: `settings`, params: { search: "groupId=='alert'" } });

  const cutOrderIds = order?.orderFlowList
    ?.filter(of => of.type === OrderFlowType.CUTPLAN)
    .map(e => e.id)
    .sort();
  const { data: layRowData = [] } = useQuery(
    !isEmpty(cutOrderIds) && { url: "lay-rows", params: { search: `completed=='TRUE';lay.orderFlow.id=in=(${cutOrderIds.join(",")})` } }
  );

  const { data: rejectionData = [] } = useQuery(
    !isEmpty(cutOrderIds) && { url: "rejections", params: { search: `orderFlow.id=in=(${cutOrderIds.join(",")})`, size: 2000 } }
  );

  const summaryColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "ERP No." },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style Name", filterOptions: { filter: true }, highlight: true },
      { dataKey: "styleNo", title: "Style No", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "orderQty", title: "Order Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "extra", title: "Extra Ship (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      {
        dataKey: "cutQty",
        title: "Cut Qty",
        render: (value, _, row) => (isEmpty(value) ? "" : `${value} ${row.uomQty || "Pc"}${!isEmpty(row.cutPercent) ? ` (${row.cutPercent}%)` : ""}`)
      },
      { dataKey: "rejectedQty", title: "Rejected Qty" },
      { dataKey: "bomCu", title: "BOM CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "planCu", title: "Plan CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "stdCu", title: "Std. CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "actualCu", title: "Actual CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "grossCu", title: "Gross CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "markerEfficiency", title: "Marker Efficiency", render: value => (isEmpty(value) ? "-" : `${value}%`) },
      { dataKey: "status", title: "Status" }
    ];
    let fields = [...orderFields, ...flowInfoFields, ...otherFields];
    if (!showStdCu) {
      fields = fields.filter(f => f !== "stdCu");
    }
    return cols.filter(c => fields.includes(c.dataKey));
  }, [orderFields, flowInfoFields, showStdCu]);

  const summaryRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    const flowInfoList = getReportFlowInfoList(order);

    const rows = flowInfoList.flatMap(({ part, ...flowInfo }) => {
      if (flowInfo.isParent) {
        return [
          {
            ...flowInfo,
            poRef: order.poRef,
            customerName: order.customer?.name,
            seasonName: order.season?.name,
            productName: flowInfo.product?.name
          }
        ];
      }

      const cutOrderList = flowInfo.cutOrderList;
      const cutOrderIds = cutOrderList.map(e => e.id);
      const status = cutOrderList.sort((a, b) => a.stateValue < b.stateValue)[0]?.state;
      const currReportList = reportList.filter(r => cutOrderIds.includes(r.cutOrderId)).filter(r => r.styleColor === flowInfo.styleColor);

      const orderQty = flowInfo.orderQty || 0;
      const cutQty = currReportList.reduce((acc, r) => acc + r.cutQty, 0);
      const cutPercent = orderQty > 0 ? toFixed((cutQty * 100) / orderQty, 1) : 0;
      const rejectedQty = currReportList.reduce((acc, r) => acc + r.rejectedQty, 0);

      //* Calculate Marker Efficiency **//
      const layMarkerList = cutOrderList
        .flatMap(
          cutOrder =>
            cutOrder.markerList
              ?.filter(m => [MarkerType.FINAL, MarkerType.REMNANT, MarkerType.MANUAL, MarkerType.RE_CUTTING].includes(m.markerType))
              .map(m => ({ ...m, plies: m.markerPlyList?.find(mp => mp.styleColor === flowInfo.styleColor)?.actualPlies || 0 })) || []
        )
        .filter(m => !isEmpty(m.layLength) && !isEmpty(m.efficiency));
      const effQtyList = layMarkerList.map(m => ({ key: m.efficiency, weight: m.noOfWays * m.plies }));
      const markerEfficiency = getWeightedMean(effQtyList);

      const fabricUom = currReportList[0]?.fabricUom;

      //** Fabric Usage Summary **//
      const itemCode = currReportList[0]?.itemCode;
      const itemColor = currReportList[0]?.fabColor;
      const allocated =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.allocated, 0)
          : currReportList.reduce((acc, r) => acc + r.allocatedWeight, 0);
      const blanketQty =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.blanketQty, 0)
          : currReportList.reduce((acc, r) => acc + r.blanketWeight, 0);
      const issued =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.issued, 0)
          : currReportList.reduce((acc, r) => acc + r.issuedWeight, 0);
      const returned =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.returned, 0)
          : currReportList.reduce((acc, r) => acc + r.returnedWeight, 0);
      const excess = fabricUom === UOM.METER.value ? currReportList.reduce((acc, r) => acc + r.excess, 0) : 0;
      const shortage = fabricUom === UOM.METER.value ? currReportList.reduce((acc, r) => acc + r.shortage, 0) : 0;
      const missing =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.missing, 0)
          : currReportList.reduce((acc, r) => acc + r.missingWeight, 0);
      const unused =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.unused, 0)
          : currReportList.reduce((acc, r) => acc + r.unusedWeight, 0);
      const usedInLay =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.usedInLay, 0)
          : currReportList.reduce((acc, r) => acc + r.usedInLayWeight, 0);
      const usedInPanel =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.usedInPanel, 0)
          : currReportList.reduce((acc, r) => acc + r.usedInPanelWeight, 0);
      const endLoss =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.endLoss, 0)
          : currReportList.reduce((acc, r) => acc + r.endLossWeight, 0);
      const damaged =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.damaged, 0)
          : currReportList.reduce((acc, r) => acc + r.damagedWeight, 0);
      const netEndBits =
        fabricUom === UOM.METER.value
          ? currReportList.reduce((acc, r) => acc + r.netEndBits, 0)
          : currReportList.reduce((acc, r) => acc + r.netEndBitsWeight, 0);
      const usedInMarker = usedInLay - endLoss;

      const stdCuAllowanceList = cutOrderList.map(e => e.orderConfig.stdCuAllowance).filter(e => !isEmpty(e) && e > 0);
      const stdCuAllowance = stdCuAllowanceList.length > 0 ? stdCuAllowanceList.reduce((acc, e) => acc + e, 0) / stdCuAllowanceList.length : 0;

      const planCuData = currReportList.map(r => ({
        key: fabricUom === UOM.METER.value ? r.planCu : r.planCuInKg,
        weight: getApprovedQty(
          cutOrderList.find(c => c.id === r.cutOrderId),
          flowInfo.styleColor
        )
      }));
      const planCu = getWeightedMean(planCuData);

      const bomCuData = currReportList.map(r => ({
        key: fabricUom === UOM.METER.value ? r.orderingCu : r.orderingCuInKg,
        weight: getApprovedQty(
          cutOrderList.find(c => c.id === r.cutOrderId),
          flowInfo.styleColor
        )
      }));
      const bomCu = getWeightedMean(bomCuData);

      const layCu = cutQty > 0 ? usedInLay / cutQty : 0;
      const markerCu = cutQty > 0 ? usedInMarker / cutQty : 0;
      const stdCu = stdCuAllowance > 0 ? (1 + stdCuAllowance * 0.01) * markerCu : undefined;
      const actualCu = cutQty > 0 ? (usedInLay + damaged + usedInPanel + netEndBits) / cutQty : 0;
      const grossCu = cutQty > 0 ? (issued + blanketQty - returned) / (cutQty - rejectedQty) : 0;
      if (isEmpty(itemCode) && isEmpty(itemColor)) return [];

      return [
        {
          ...flowInfo,
          flowInfo,
          part,
          // key: `${flowInfo.id}-${part.effPartName}`,
          cutOrderList,
          status,
          poRef: order.poRef,
          customerName: order.customer?.name,
          seasonName: order.season?.name,
          effPartName: part.effPartName,
          cutQty,
          cutPercent,
          rejectedQty,
          fabricUom,
          bomCu: toFixed(bomCu, 3),
          layCu: toFixed(layCu, 3),
          stdCu: !isEmpty(stdCu) ? toFixed(stdCu, 3) : undefined,
          planCu: toFixed(planCu, 3),
          actualCu: toFixed(actualCu, 3),
          grossCu: toFixed(grossCu, 3),
          markerEfficiency: toFixed(markerEfficiency, 2),
          itemCode,
          itemColor,
          allocated: toFixed(allocated, 1),
          issued: toFixed(issued, 1),
          blanketQty: toFixed(blanketQty, 2),
          returned: toFixed(returned, 1),
          excess: toFixed(excess, 1),
          shortage: toFixed(shortage, 1),
          missing: toFixed(missing, 1),
          unused: toFixed(unused, 1),
          usedInLay: toFixed(usedInLay, 1),
          usedInMarker: toFixed(usedInMarker, 1),
          usedInPanel: toFixed(usedInPanel, 1),
          endLoss: toFixed(endLoss, 1),
          damaged: toFixed(damaged, 1),
          netEndBits: toFixed(netEndBits, 1)
        }
      ];
    });

    return rows;
  }, [order, productParts, reportList]);

  useDeepEffect(() => {
    if (isEmpty(summaryRows) || !isEmpty(selectedKey)) return;
    if (isEmpty(style) && isEmpty(color)) {
      setSelectedKey(summaryRows.filter(e => isFalse(e.isParent))[0]?.key);
    } else {
      setSelectedKey(summaryRows.filter(e => isFalse(e.isParent)).find(e => e.style === style && e.color === color).key);
    }
  }, [summaryRows, selectedKey, style, color]);

  const selectedRow = summaryRows?.find(e => e.key === selectedKey);
  const fabricUom = reportList[0]?.fabricUom;

  const cutOrderRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(order?.orderFlowList) || isEmpty(selectedRow)) return [];
    const cutOrderIds = selectedRow.cutOrderList?.map(e => e.id) || [];
    let rows = getCutOrderRows(order, productParts);
    return rows
      .filter(row => cutOrderIds.includes(row.cutOrder?.id))
      .map(row => ({
        ...row,
        deliveryDate: !isEmpty(row?.deliveryDate)
          ? listFromCsv(row.deliveryDate)
              .map(date => format(new Date(date), "dd MMM yy"))
              .join(", ")
          : ""
      }))
      .map(row => {
        if (!isEmpty(row.parentKey)) return { ...row, bomCu: null, state: null };

        const cutOrder = row.cutOrder || {};
        const report = reportList.find(r => cutOrder.id === r.cutOrderId && r.styleColor === selectedRow?.styleColor) || {};

        const orderQty = report.orderQty || 0;
        const cutQty = report.cutQty || 0;
        const cutPercent = orderQty > 0 ? toFixed((cutQty * 100) / orderQty, 1) : 0;

        if (report.fabricUom === UOM.METER.value) {
          return {
            ...row,
            report,
            fabricUom: report.fabricUom,
            cutQty,
            cutPercent,
            rejectedQty: report.rejectedQty || 0,
            bomCu: toFixed(report?.orderingCu, 3),
            layCu: toFixed(report?.layCu, 3),
            planCu: toFixed(report?.planCu, 3),
            stdCu: !isEmpty(report?.stdCu) ? toFixed(report?.stdCu, 3) : "",
            actualCu: toFixed(report?.actualCu, 3),
            grossCu: toFixed(report?.grossCu, 3),
            markerEfficiency: toFixed(report?.markerEfficiency, 2),
            itemCode: report.itemCode,
            itemColor: report.fabColor,
            allocated: toFixed(report.allocated, 1),
            blanketQty: toFixed(report.blanketQty, 2),
            issued: toFixed(report.issued, 1),
            returned: toFixed(report.returned, 1),
            excess: toFixed(report.excess, 1),
            shortage: toFixed(report.shortage, 1),
            missing: toFixed(report.missing, 1),
            unused: toFixed(report.unused, 1),
            usedInLay: toFixed(report.usedInLay, 1),
            usedInMarker: toFixed((report.usedInLay || 0) - (report.endLoss || 0), 1),
            usedInPanel: toFixed(report.usedInPanel, 1),
            endLoss: toFixed(report.endLoss, 1),
            damaged: toFixed(report.damaged, 1),
            netEndBits: toFixed(report.netEndBits, 1)
          };
        } else {
          return {
            ...row,
            report,
            fabricUom: report.fabricUom,
            cutQty,
            cutPercent,
            rejectedQty: report.rejectedQty || 0,
            bomCu: toFixed(report?.orderingCuInKg, 3),
            layCu: toFixed(report?.layCuInKg, 3),
            planCu: toFixed(report?.planCuInKg, 3),
            stdCu: !isEmpty(report?.stdCuInKg) ? toFixed(report?.stdCuInKg, 3) : "",
            actualCu: toFixed(report?.actualCuInKg, 3),
            grossCu: toFixed(report?.grossCuInKg, 3),
            markerEfficiency: toFixed(report?.markerEfficiency, 2),
            itemCode: report.itemCode,
            itemColor: report.fabColor,
            allocated: toFixed(report.allocatedWeight, 1),
            blanketQty: toFixed(report.blanketWeight, 2),
            issued: toFixed(report.issuedWeight, 1),
            returned: toFixed(report.returnedWeight, 1),
            missing: toFixed(report.missingWeight, 1),
            unused: toFixed(report.unusedWeight, 1),
            usedInLay: toFixed(report.usedInLayWeight, 1),
            usedInMarker: toFixed((report.usedInLayWeight || 0) - (report.endLossWeight || 0), 1),
            usedInPanel: toFixed(report.usedInPanelWeight, 1),
            endLoss: toFixed(report.endLossWeight, 1),
            damaged: toFixed(report.damagedWeight, 1),
            netEndBits: toFixed(report.netEndBitsWeight, 1)
          };
        }
      });
  }, [order, productParts, selectedRow]);

  useDeepEffect(() => {
    if (isEmpty(cutOrderRows)) return;
    setCutOrderKey(cutOrderRows[0]?.key);
  }, [cutOrderRows]);

  const cutOrderColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "fpo", title: "FPO", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      // { dataKey: "productName", title: "Product", filterOptions: { filter: true } },
      // { dataKey: "style", title: "Style", filterOptions: { filter: true } },
      // { dataKey: "styleColor", title: "Style Color", filterOptions: { filter: true } },
      // { dataKey: "effPartName", title: "Part" },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "inseam", title: "Inseam", render: (value, shortValue) => shortValue || "-", filterOptions: { filter: true }, highlight: true },
      {
        dataKey: "destination",
        title: "Destination",
        render: (value, shortValue) => shortValue || "-",
        filterOptions: { filter: true },
        highlight: true
      },
      {
        dataKey: "deliveryDate",
        title: "Delivery Date",
        render: (value, shortValue) => shortValue || "-",
        filterOptions: { filter: true },
        highlight: true
      },
      { dataKey: "delMode", title: "Delivery Mode", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "extra", title: "Extra (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      { dataKey: "fpoQty", title: "FPO Qty" },
      {
        dataKey: "cutQty",
        title: "Cut Qty",
        render: (value, _, row) => (isEmpty(value) ? "" : `${value}${!isEmpty(row.cutPercent) ? ` (${row.cutPercent}%)` : ""}`)
      },
      { dataKey: "rejectedQty", title: "Rejected Qty" },
      { dataKey: "bomCu", title: "BOM CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "planCu", title: "Plan CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "stdCu", title: "Std. CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "actualCu", title: "Actual CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "grossCu", title: "Gross CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "markerEfficiency", title: "Marker Efficiency", render: value => (isEmpty(value) ? "-" : `${value}%`) },
      { dataKey: "state", title: "Status", filterOptions: { filter: true }, length: 100 }
    ];

    if (cutOrderRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (cutOrderRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (cutOrderRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (cutOrderRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (cutOrderRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (cutOrderRows.every(e => isEmpty(e.delMode))) {
      cols = cols.filter(c => c.dataKey !== "delMode");
    }
    if (cutOrderRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }
    if (!showStdCu) {
      cols = cols.filter(c => c.dataKey !== "stdCu");
    }

    return cols;
  }, [cutOrderRows, allUnitList, showStdCu]);

  const fabUsageColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "itemCode", title: "Item Code" },
      { dataKey: "itemColor", title: "Item Color" },
      { dataKey: "allocated", title: "Allocated", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      {
        dataKey: "blanketQty",
        title: "Testing Loss",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      { dataKey: "issued", title: "Issued Qty", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "returned", title: "Returned", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      {
        dataKey: "excess",
        title: "Excess",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${toFixedSigned(value)} ${UomCodeFromValue[row.fabricUom]}`)
      },
      { dataKey: "shortage", title: "Shortage", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "missing", title: "Missing", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "unused", title: "Unused", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      {
        dataKey: "usedInMarker",
        title: "Used in Marker",
        render: (value, _, row) => (isEmpty(value) || isEmpty(row.itemCode) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      {
        dataKey: "usedInPanel",
        title: "Used in Panel",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      },
      { dataKey: "endLoss", title: "End Loss", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      { dataKey: "damaged", title: "Damaged", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`) },
      {
        dataKey: "netEndBits",
        title: "Net End Bits",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.fabricUom]}`)
      }
    ];

    if (cutOrderRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (cutOrderRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (cutOrderRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (cutOrderRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (cutOrderRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (cutOrderRows.every(e => isEmpty(e.delMode))) {
      cols = cols.filter(c => c.dataKey !== "delMode");
    }
    if (cutOrderRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }
    if (fabricUom === UOM.KILOGRAM.value) {
      cols = cols.filter(c => !["shortage", "excess"].includes(c.dataKey));
    }

    const fields = [...orderFields, ...flowInfoFields, ...otherFields];
    return cols.filter(c => fields.includes(c.dataKey));
  }, [orderFields, flowInfoFields, cutOrderRows, allUnitList, fabricUom]);

  const fabUsageRows = cutOrderRows.filter(e => isEmpty(e.parentKey));

  const { cutOrder, report } = cutOrderRows.find(e => e.key === cutOrderKey) || {};

  const { columnsCutSummary, headerRows } = useDeepMemo(() => {
    if (isEmpty(order) || isEmpty(cutOrder)) return { columnsCutSummary: [], headerRows: [] };

    const sizeBreakupList = findSizeBreakupList(cutOrder, order);

    const sizeGroups = getDistinctValues(sizeBreakupList.sort((a, b) => a.serialNo - b.serialNo).map(sb => sb.sizeGroup));

    const styleColorIdx = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});
    const groupedCutPlan = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

    let groupRow = {};
    let sizeGroup;
    const sizeList = sizeBreakupList.map((sb, idx) => {
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sb.sizeKey] = sb.sizeGroup;
      }
      return {
        dataKey: sb.sizeKey,
        title: sb.size,
        align: "center",
        styleColorIdx: sizeGroups.length > 1 ? styleColorIdx[sb.sizeGroup] : undefined,
        leftBorder,
        rightBorder: idx === sizeBreakupList.length - 1,
        colSpan: ({ column, row, rowIdx }) =>
          !groupedCutPlan || rowIdx === 1
            ? 1
            : isEmpty(row[column.dataKey])
              ? 0
              : getDistinctValues(sizeBreakupList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
      };
    });

    let columns = [{ dataKey: "desc", title: "Description", align: "left" }, ...sizeList, { dataKey: "total", title: "Total", leftBorder: true }];

    columns = columns.map(c => ({ ...c, align: c.align || "center" }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    } else {
      headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    }

    return { columnsCutSummary: columns, headerRows };
  }, [order, cutOrder]);

  const rowsCutSummary = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(cutOrder)) return [];
    const style = selectedRow?.style;
    const color = selectedRow?.color;
    const flowInfoList = getCutOrderFlowInfoList(order, cutOrder).filter(e => e.style === style && e.color === color);

    const sizeBreakupList = flowInfoList.flatMap((fi, idx) => fi.fpoSizeBreakupList || []);
    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sizeKeyList = Object.values(sbMap)
      .sort((a, b) => a.serialNo - b.serialNo)
      .map(sb => sb.sizeKey);

    let rows = [];
    let rejectedTotalRows = [];

    // Process Order Qty
    const orderQty = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), {});
    let totalOrderQty = Object.keys(orderQty).reduce((acc, key) => acc + orderQty[key], 0);
    rows.push({
      ...orderQty,
      desc: lodashGet(messages, "resources.orders.fields.fpoQty", "FPO Qty"),
      total: totalOrderQty,
      totalPercent: "100.0%"
    });

    let commCutQty = {};

    const prevLotState = mapFromCsv(cutOrder.prevLotState, ";");
    const lotValues = Object.keys(prevLotState);
    const prevFlowList = getOrderFlowList(order, cutOrder)
      .filter(oc => oc.part === cutOrder.part && lotValues.includes(oc.lotValue))
      .sort((a, b) => a.plannedAt - b.plannedAt);
    const orderFlowList = [...prevFlowList, cutOrder];

    for (let i = 0; i < orderFlowList?.length; i++) {
      const flow = orderFlowList[i];
      const markerList = getMarkerList(flow);

      const layRowList =
        layRowData
          ?.filter(row => isTrue(row.completed))
          ?.map(row => ({ ...row, marker: markerList?.find(m => m.id === row.markerId) }))
          .filter(e => !isEmpty(e.marker)) || [];

      const cutQty = sizeKeyList.reduce((acc, sizeKey) => {
        const qty = layRowList
          .filter(row => row.styleColor === selectedRow.styleColor)
          .reduce((acc, row) => {
            const sizeRatio = row.marker?.sizeRatioList.find(sr => sr.sizeKey === sizeKey);
            const plies = SpreadingType.FACE_ONE_WAY !== row.marker?.spreadingType ? row.actualPly / 2 : row.actualPly;
            const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
            return acc + qty;
          }, 0);
        return { ...acc, [sizeKey]: qty };
      }, {});

      // sizeKeyList.forEach(sizeKey => {
      //   const rejectedQty = rejectionList.filter(r => r.styleColor === styleColor && r.sizeKey === sizeKey).length;
      //   cutQty[sizeKey] = cutQty[sizeKey] - rejectedQty;
      // });

      // cumulative cut qty
      sizeKeyList.forEach(sizeKey => {
        commCutQty[sizeKey] = (commCutQty[sizeKey] || 0) + cutQty[sizeKey];
      });

      let total = Object.keys(cutQty).reduce((acc, key) => acc + cutQty[key], 0);
      let totalPercent = `${toFixed((total * 100) / totalOrderQty, 1)}%`;
      rows.push({ ...cutQty, desc: `Cut Qty, ${flow.lotValue}`, total, totalPercent });

      const rejectedQty = sizeKeyList
        .map(key => {
          const perSizeReject = rejectionData
            .filter(row => row.styleColor === selectedRow.styleColor)
            .filter(e => e.orderFlowId === flow.id)
            .filter(e => e.sizeKey === key);
          const qty = perSizeReject.reduce((acc, curr) => acc + curr?.qty, 0);
          return {
            [key]: qty
          };
        })
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});

      total = Object.values(rejectedQty)?.reduce((acc, curr) => acc + curr, 0);
      let totalPer = totalOrderQty > 0 ? `${toFixed((total * 100) / totalOrderQty, 1)}%` : "";
      rows.push({ ...rejectedQty, desc: `Rejected Qty ${flow.lotValue}`, total, totalPercent: totalPer, key: flow.lotValue });
      rejectedTotalRows.push({ ...rejectedQty, desc: `Rejected Qty ${flow.lotValue}`, total, totalPercent: totalPer, key: flow.lotValue });

      const previousRejectedRows = rows.filter(e => e.desc?.includes("Rejected Qty") && e.key !== flow.lotValue);
      rows = rows.filter(e => !(e.desc?.includes("Rejected Qty") && e.key !== flow.lotValue));

      if (!isEmpty(previousRejectedRows) && previousRejectedRows.length > 1) {
        const rejectedQty = sizeKeyList
          .map(key => {
            return { [key]: previousRejectedRows.map(e => e[key]).reduce((a, b) => a + b, 0) };
          })
          .reduce((acc, curr) => ({ ...acc, ...curr }), {});
        let total = Object.values(rejectedQty)?.reduce((acc, curr) => acc + curr, 0);
        let totalPer = totalOrderQty > 0 ? `${toFixed((total * 100) / totalOrderQty, 1)}%` : "";
        rows.push({
          ...rejectedQty,
          desc: `Rejected Qty ${previousRejectedRows[previousRejectedRows?.length - 1]?.key}`,
          total,
          totalPercent: totalPer
        });
      }
    }

    //Net rejected Qty ;
    const rejectedQty = sizeKeyList
      .map(key => {
        return { [key]: rejectedTotalRows.map(e => e[key]).reduce((a, b) => a + b, 0) };
      })
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});
    let total = Object.values(rejectedQty)?.reduce((acc, curr) => acc + curr, 0);
    let totalPer = totalOrderQty > 0 ? `${toFixed((total * 100) / totalOrderQty, 1)}%` : "";
    if (rejectedTotalRows.length > 1) {
      rows.push({ ...rejectedQty, desc: `Total Rejected Qty`, total, totalPercent: totalPer });
    }

    //Net Cut Qty
    const cutQtyRow = rows.filter(e => e.desc.includes("Cut Qty"));
    const cutQty = sizeKeyList
      .map(key => {
        const ctRow = cutQtyRow.map(e => e[key]);
        return { [key]: ctRow.reduce((a, b) => a + b, 0) };
      })
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});

    const netCutQty = sizeKeyList
      .map(key => {
        const sizeRejectCutQty = rejectedQty[key] || 0;
        const sizeCutQty = cutQty[key] || 0;
        return { [key]: sizeCutQty - sizeRejectCutQty };
      })
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});

    total = Object.values(netCutQty)?.reduce((acc, curr) => acc + curr, 0);
    totalPer = totalOrderQty > 0 ? `${toFixed((total * 100) / totalOrderQty, 1)}%` : "";
    rows.push({ ...netCutQty, desc: `Net Cut Qty`, total, totalPercent: totalPer });

    if (!isEmpty(lotValues)) {
      let totalQty = sizeKeyList.reduce((acc, sizeKey) => ({ ...acc, [sizeKey]: netCutQty[sizeKey] }), {});
      let total = Object.keys(totalQty).reduce((acc, key) => acc + totalQty[key], 0);
      const totalPercent = `${toFixed((total * 100) / totalOrderQty, 1)}%`;
      rows.push({ ...totalQty, total, totalPercent, desc: "Total" });
    }

    // Process Balance Qty
    let totalBalance = 0;
    let balanceQty = sizeKeyList.reduce((acc, sizeKey) => {
      const balance = netCutQty[sizeKey] - orderQty[sizeKey];
      totalBalance += balance;
      return { ...acc, [sizeKey]: `${signedValue(balance)}(${toFixed((balance * 100) / orderQty[sizeKey], 1)}%)` };
    }, {});
    const totalPercent = `${toFixed((totalBalance * 100) / totalOrderQty, 1)}%`;
    rows.push({ ...balanceQty, total: `${signedValue(totalBalance)}(${totalPercent})`, totalPercent, desc: "Extra/Short" });

    return rows;
  }, [order, cutOrder, layRowData, selectedRow, rejectionData]);

  const chartOptions = useDeepMemo(() => getEchartOptions({ cutOrder, report }), [cutOrder, report]);

  const { otherInfoList, highlightList } = useDeepMemo(() => getOtherInfoAndHighlight(cutOrder, report, settings), [cutOrder, report, settings]);

  const rowsRemarks = useDeepMemo(() => {
    if (isEmpty(cutOrder)) return [];

    const alerts = cutOrder.orderConfig.alerts ? JSON.parse(cutOrder.orderConfig.alerts) : [];
    const merchantName = cutOrder.orderConfig.cutPlanApprovedBy;
    const cadPersonName = cutOrder.orderConfig.cadRemarksBy;
    const alertParamMap = Object.values(AlertParemeter).reduce((acc, e) => ({ ...acc, [e.id]: e.name }), {
      fabricLeftover: "Fabric Left Over",
      higherRecut: "High Recutting",
      higherEndBits: "High End bits"
    });
    let remarksData = [
      ...alerts.map(alert => [
        alertParamMap[alert.parameter] || alert.parameter,
        alert.reasons.join(", "),
        alert.parameter === AlertParemeter.CUT_PLAN_QTY.id ? merchantName : cadPersonName
      ]),
      ["CAD Remarks", cutOrder.orderConfig.cadRemarks || "-", cadPersonName],
      [
        "Merchant Remarks",
        cutOrder.orderConfig.merchantRemarks || "-",
        `${cutOrder.orderConfig.cutPlanApproval === "Approved" ? merchantName : "Not Approved"}`
      ],
      ["Cutting Remarks", cutOrder.orderConfig.cuttingRemarks || "-", cutOrder.orderConfig.cuttingRemarksBy],
      ["Warehouse Remarks", cutOrder.orderConfig.warehouseRemarks || "-", cutOrder.orderConfig.warehouseRemarksBy]
    ];

    const rows = remarksData.map(data => ({
      params: data[0],
      remarks: data[1],
      user: data[2]
    }));

    return rows;
  }, [cutOrder]);

  const onPrint = async () => {
    setPrinting(true);

    const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

    const sizeBreakupList = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []);
    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sizeCharacterCount = Object.values(sbMap).reduce((acc, sb) => acc + maxCharacterCount(sb.size), 0);

    const charactersPerPage = 50;
    let pageFormat = "a4";
    if (Math.ceil(sizeCharacterCount / charactersPerPage) === 1) {
      pageFormat = "a4";
    } else if (Math.ceil(sizeCharacterCount / charactersPerPage) === 2) {
      pageFormat = "a3";
    } else if (Math.ceil(sizeCharacterCount / charactersPerPage) === 3) {
      pageFormat = "a2";
    } else {
      pageFormat = "a1";
    }
    const chartHeight = pageFormat === "a4" ? 65 : pageFormat === "a3" ? 80 : pageFormat === "a2" ? 100 : 130; // Minimum required height for chart

    var doc = new jsPDF("l", "mm", pageFormat);

    const unit = allUnitList.find(e => e?.unitId === selectedRow?.unitIds) || {};
    setDefault(doc, "Reconciliation Report", tenant, null, unit);
    const suffix = `${order.customer.name}, ${order.poRef}, ${selectedRow.styleColor}`;

    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    let startY = 30;
    const config = { scale: 1.2, logging: false };

    ////// Print Order Info //////////////
    const extraIdx = summaryColumns.findIndex(c => c.dataKey === "extra");
    let columns = summaryColumns.slice(0, extraIdx + 1).map(({ dataKey, title }) => ({
      dataKey,
      header: orderFields.includes(dataKey)
        ? lodashGet(messages, `resources.orders.fields.${dataKey}`, title)
        : lodashGet(messages, `resources.orders.fields.flowInfo.${dataKey}`, title)
    }));

    doc.setFontSize(10);
    doc.text("Order Info", 7, startY);
    doc.autoTable({ columns: [...columns], body: [selectedRow], startY: startY + 2 });

    ////// Print Cut Order Info //////////////
    startY = doc.autoTable.previous.finalY + 7;
    columns = cutOrderColumns.map(({ dataKey, title }) => ({
      dataKey,
      header: orderFields.includes(dataKey)
        ? lodashGet(messages, `resources.orders.fields.${dataKey}`, title)
        : lodashGet(messages, `resources.orders.fields.flowInfo.${dataKey}`, title)
    }));
    const parentCutOrder = cutOrderRows.find(e => e.key === cutOrderKey);
    const children = cutOrderRows.filter(e => e.parentKey === cutOrderKey);
    let body = [parentCutOrder, ...children];

    doc.setFontSize(10);
    doc.text("Cut Order", 7, startY);
    doc.autoTable({
      columns: [...columns],
      body: [...body],
      startY: startY + 2,
      didParseCell: function (data) {
        if (data.row.section === "body" && data.row.index === 0) {
          data.cell.styles.fillColor = [241, 241, 241];
        }
      }
    });

    ////// Print Fabric Usage Info //////////////
    startY = doc.autoTable.previous.finalY + 7;
    columns = fabUsageColumns.map(({ dataKey, title }) => ({ dataKey, header: title }));
    body = [parentCutOrder];

    doc.setFontSize(10);
    doc.text("Fabric Usage Summary", 7, startY);
    doc.autoTable({ columns: [...columns], body: [...body], startY: startY + 2 });

    // Cut Summary
    body = rowsCutSummary;
    columns = columnsCutSummary.map(c => ({ ...c, header: c.title }));
    const sbList = findSizeBreakupList(cutOrder, order);

    let head = [];
    if (headerRows?.length === 2) {
      const sizgeGroupRow = columns
        .map(c =>
          ["label", "total", "totalPercent"].includes(c.dataKey)
            ? ""
            : isEmpty(headerRows[0][c.dataKey])
              ? undefined
              : {
                  content: headerRows[0][c.dataKey],
                  colSpan: getDistinctValues(sbList.filter(sb => sb.sizeGroup === headerRows[0][c.dataKey]).map(sb => sb.sizeKey))?.length,
                  styles: { halign: "center" }
                }
        )
        .filter(e => e !== undefined);
      head.push(sizgeGroupRow);
      head.push(columns.map(c => headerRows[1][c.dataKey]));
    } else {
      head.push(columns.map(c => headerRows[0][c.dataKey]));
    }

    body = body.map(row => columns.map(c => row[c.dataKey]));

    startY = doc.autoTable.previous.finalY + 7;
    doc.text("Cut Summary", 7, startY);
    doc.autoTable({ head, body: body, startY: startY + 2 });

    //* First Row Chart *//
    const rowImageOne = document.getElementById("row-1");
    const rowImageTwo = document.getElementById("row-2");
    const rowImageThree = document.getElementById("row-3");

    startY = doc.autoTable.previous.finalY + 3;

    if (pageHeight - startY < chartHeight + 10) {
      doc.addPage();
      startY = 10;
    }

    let chartWidth = (pageWidth - 7 * 4) / 3;

    const canvas1 = await html2canvas(rowImageOne, config);
    const imgData1 = canvas1.toDataURL("image/png");
    doc.addImage(imgData1, "PNG", 7, startY, chartWidth, chartHeight, undefined, "FAST");

    const canvas2 = await html2canvas(rowImageTwo, config);
    const imgData2 = canvas2.toDataURL("image/png");
    doc.addImage(imgData2, "PNG", chartWidth + 14, startY, chartWidth, chartHeight, undefined, "FAST");

    const canvas3 = await html2canvas(rowImageThree, config);
    const imgData3 = canvas3.toDataURL("image/png");
    doc.addImage(imgData3, "PNG", chartWidth * 2 + 21, startY, chartWidth, chartHeight, undefined, "FAST");

    startY = startY + chartHeight + 3;
    if (pageHeight - startY < chartHeight + 10) {
      doc.addPage();
      startY = 10;
    }

    //* Second Row Chart *//
    const RollWidthDeviationImg = document.getElementById("deviation");
    const RollLengthVariationImg = document.getElementById("Variation");
    const MarkerEfficiencyImg = document.getElementById("marker");

    chartWidth = (pageWidth - 7 * 4) / 4;

    const canvas4 = await html2canvas(RollWidthDeviationImg, config);
    const imgData4 = canvas4.toDataURL("image/png");
    doc.addImage(imgData4, "PNG", 7, startY, chartWidth * 2, chartHeight, undefined, "FAST");

    const canvas5 = await html2canvas(RollLengthVariationImg, config);
    const imgData5 = canvas5.toDataURL("image/png");
    doc.addImage(imgData5, "PNG", chartWidth * 2 + 14, startY, chartWidth, chartHeight, undefined, "FAST");

    const canvas6 = await html2canvas(MarkerEfficiencyImg, config);
    const imgData6 = canvas6.toDataURL("image/png");
    doc.addImage(imgData6, "PNG", chartWidth * 3 + 21, startY, chartWidth, chartHeight, undefined, "FAST");

    //** Fabric Utilization Details **//
    let tableWidth = (pageWidth - 21) / 3;
    startY = startY + chartHeight + 5;
    const minFabUtilizationTableHeight = 100;
    if (pageHeight - startY < minFabUtilizationTableHeight) {
      doc.addPage();
      startY = 10;
    }

    columns = colsFabUtilization.map(({ dataKey, title }) => ({ dataKey, header: title }));
    let fabricUtilisationData = chartOptions[DataType.FABRIC_UTILIZATION_DETAILS] || [];
    body = fabricUtilisationData.map(row => columns.map(c => row[c.dataKey]));
    doc.text("Fabric Utilization Details", 7, startY);
    doc.autoTable({ columns, body, startY: startY + 2, tableWidth: tableWidth * 2 });

    const fabUtilizationY = doc.autoTable.previous.finalY;

    // Highlights
    doc.autoTable({
      head: [[{ content: "Highlights", colSpan: 2 }]],
      body: highlightList.map(({ title, rightTitle }) => [title, rightTitle]),
      margin: { left: tableWidth * 2 + 14 },
      tableWidth: tableWidth,
      startY: startY + 2
    });

    // Other Info
    doc.autoTable({
      head: [[{ content: "Other Informations", colSpan: 2 }]],
      body: otherInfoList.map(({ title, rightTitle }) => [title, rightTitle]),
      margin: { left: tableWidth * 2 + 14 },
      tableWidth: tableWidth,
      startY: doc.autoTable.previous.finalY + 7
    });

    const otherInfoY = doc.autoTable.previous.finalY;

    startY = Math.max(fabUtilizationY, otherInfoY);
    startY = startY + 7;
    if (pageHeight - startY < minReqHeight) {
      doc.addPage();
      startY = 10;
    }

    // Remarks Table
    columns = colsRemarks.map(c => ({ dataKey: c.dataKey, header: c.title }));

    doc.setFontSize(10);
    doc.text("Remarks", 7, startY);
    doc.autoTable({ columns: [...columns], body: rowsRemarks, startY: startY + 3 });

    doc.putTotalPages(totalPagesExp);
    let filename = `RECONCILIATION REPORT - ${suffix}.pdf`;
    filename = filename.replace(/\//g, "_");
    doc.save(filename);

    setPrinting(false);
  };

  return {
    cutOrder,
    summaryColumns,
    summaryRows,
    cutOrderRows,
    cutOrderColumns,
    fabUsageColumns,
    fabUsageRows,
    columnsCutSummary,
    headerRows,
    rowsCutSummary,
    colsFabUtilization,
    colsRemarks,
    chartOptions,
    otherInfoList,
    highlightList,
    rowsRemarks,
    cutOrderKey,
    printing,
    setCutOrderKey,
    onPrint
  };
};

export default useReconciliationDetail;
